import React, { useState } from "react";
import useGetUsers from "../../../hooks/masters/users/useGetUsers";
import Spinner from "../../../components/spinner";
import Errors from "../../errors";
import { RiPencilFill } from "react-icons/ri";
import { IoTrashBin } from "react-icons/io5";
import { convertToSentenceCase } from "../../../utilities";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import useDeleteUsers from "../../../hooks/masters/users/useDeleteUsers";
import ConfirmationModal from "../../../components/elements/ConfirmationModal";
import { Dialog } from "@mui/material";
import { RiLockPasswordLine } from "react-icons/ri";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import toast from "react-hot-toast";
import useCreate from "../../../hooks/useCreate";
import { PASSWORD_REGEX } from "../../../utilities/regex";

const UsersListing = () => {
	const [resetUser, setResetUser] = useState(null);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    userList,
    status,
    isUserListLoading,
    error,
    handleSearch,
    searchQuery,
  } = useGetUsers();

  const { toggleConfirmation, showConfirmation, deleteUser } = useDeleteUsers();

	const closeResetPassword = () => {
		setResetUser(null);
		setPassword("");
		setConfirmPassword("");
		setShowPassword(false);
		setShowConfirmPassword(false);
	};

	const { setDataToServer: resetPasswordRequest } = useCreate({
		url: "auth/reset_admin_password",
		onSuccess: (response) => {
			toast.success(response?.message || "Password reset successfully");
			closeResetPassword();
		},
		onError: (error) => {
			const response = error.response?.data;
			const validationMessage = response?.data
				? Object.values(response.data)[0]
				: null;
			toast.error(validationMessage || response?.message || "Password reset failed");
		},
	});

	const submitPasswordReset = (event) => {
		event.preventDefault();
		if (!PASSWORD_REGEX.test(password) || password.length < 8) {
			toast.error("Password must be at least 8 characters and include uppercase, lowercase, number and special character.");
			return;
		}
		if (password !== confirmPassword) {
			toast.error("Passwords do not match");
			return;
		}
		resetPasswordRequest.mutate({
			user_id: resetUser.user_id,
			password,
			confirm_password: confirmPassword,
		});
	};

  return (
    <div className="category px-4">
      <div className="category__head headingBorder__b">
        <h2 className="titleHeading">Users</h2>
      </div>

      {showConfirmation && (
        <ConfirmationModal
          open={showConfirmation}
          handleClose={toggleConfirmation}
          title="Delete User?"
          message="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={deleteUser}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}

	  <Dialog open={!!resetUser} onClose={closeResetPassword} maxWidth="xs" fullWidth>
		<form onSubmit={submitPasswordReset} className="p-6">
			<h2 className="text-lg font-semibold">Reset Password</h2>
			<p className="text-sm text-gray-600 mt-1">
				Set a new password for {resetUser?.name || resetUser?.email}.
			</p>
			<div className="mt-5">
				<label className="label">New Password <span className="text-red-500">*</span></label>
				<div className="relative">
					<input className="input pr-10" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" />
					<button type="button" className="absolute right-3 top-3 text-gray-500" onClick={() => setShowPassword((value) => !value)}>
						{showPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
					</button>
				</div>
			</div>
			<div className="mt-4">
				<label className="label">Confirm Password <span className="text-red-500">*</span></label>
				<div className="relative">
					<input className="input pr-10" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" />
					<button type="button" className="absolute right-3 top-3 text-gray-500" onClick={() => setShowConfirmPassword((value) => !value)}>
						{showConfirmPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
					</button>
				</div>
			</div>
			<p className="text-xs text-gray-500 mt-3">Use at least 8 characters with uppercase, lowercase, number and special character.</p>
			<div className="flex justify-end gap-3 mt-6">
				<Button type="button" variant="outlined" onClick={closeResetPassword}>Cancel</Button>
				<Button type="submit" variant="contained" disabled={resetPasswordRequest.isLoading} className="primaryBtn-solid">
					{resetPasswordRequest.isLoading ? "Resetting..." : "Reset Password"}
				</Button>
			</div>
		</form>
	  </Dialog>

      <div className="flex items-end space-x-4 my-5">
        <div className="flex-1">
          <input
            onChange={(e) => handleSearch(e.target.value)}
            value={searchQuery}
            type="text"
            placeholder="Search By Key Word...."
            className="input"
          />
        </div>
        <Link to={`add-user`} className="block">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            className="primaryBtn-outlined"
          >
            Add user
          </Button>
        </Link>
      </div>

      {isUserListLoading ? (
        <Spinner />
      ) : status === "error" ? (
        <Errors errorObject={error} inline />
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
          <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
            <thead>
              <tr className="text-left">
                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  ID
                </th>
                <td className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  <p className="w-44">Name</p>
                </td>
                <td className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  Email
                </td>
                <td className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  <p className="w-24">Phone</p>
                </td>
                <td className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  Role
                </td>
                <td className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs">
                  Actions
                </td>
              </tr>
            </thead>
            <tbody>
              {userList.map((user) => (
                <tr key={user.id} className="border-b">
                  <td className="px-4 py-2">{user.user_id}</td>
                  <td className="px-4 py-2">{user.name}</td>
                  <td className="px-4 py-2">{user.email}</td>
                  <td className="px-4 py-2">{user.phone}</td>
                  <td className="px-4 py-2">
                    {convertToSentenceCase(user.user_role)}
                  </td>
                  <td className="px-4 py-2">
                    {/* Actions can be added here */}
                    <Link
                      to={`/masters/users/edit-user/${user.user_id}`}
                      className="text-blue-500 hover:underline"
                    >
                      <RiPencilFill className="inline-block mr-1 w-5 h-5" />
                    </Link>
                    <button
					  title="Reset password"
					  className="text-amber-600 hover:underline ml-2"
					  onClick={() => setResetUser(user)}
					>
					  <RiLockPasswordLine className="inline-block w-5 h-5" />
					</button>
					<button
                      className="text-red-500 hover:underline ml-2"
                      onClick={() => toggleConfirmation(user.user_id)}
                    >
                      <IoTrashBin className="inline-block w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersListing;
